import React from 'react';
import {
    X,
    DollarSign,
    FileText,
    Calendar,
    Briefcase,
    PhoneCall,
    User,
    GraduationCap,
    MapPin,
    CheckCircle2,
    Clock,
    Award,
    Building
} from 'lucide-react';

interface InfoDisplayProps {
    type: string;
    data: any;
    onCancel: () => void;
}

export const InfoDisplay: React.FC<InfoDisplayProps> = ({ type, data, onCancel }) => {
    const renderHeader = (title: string, Icon: any, subtitle: string) => (
        <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm z-10">
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl text-white">
                    <Icon size={24} />
                </div>
                <div>
                    <h3 className="font-sans font-bold text-2xl text-gray-900 tracking-tight">{title}</h3>
                    <p className="text-xs text-indigo-600 font-bold tracking-widest uppercase mt-1">{subtitle}</p>
                </div>
            </div>
            <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
                <X size={24} />
            </button>
        </div>
    );

    const renderSection = (title: string, children: React.ReactNode) => (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
            <h4 className="font-sans font-bold text-gray-900 border-b border-gray-50 pb-2 mb-2">{title}</h4>
            {children}
        </div>
    );

    const renderListItem = (label: string, value: string | React.ReactNode, Icon?: any) => (
        <div className="flex items-start gap-4 py-3 first:pt-0 last:pb-0 border-b border-gray-50 last:border-0">
            {Icon && <Icon size={18} className="text-gray-400 mt-1 shrink-0" />}
            <div className="space-y-1">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</p>
                <div className="text-gray-900 font-medium">{value}</div>
            </div>
        </div>
    );

    const renderContent = () => {
        switch (type) {
            case 'program-details':
                return (
                    <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6 bg-gray-50/50">
                        {renderSection("Executive Summary", (
                            <p className="text-gray-600 leading-relaxed italic">"{data.description}"</p>
                        ))}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {renderSection("Structure", (
                                <div className="space-y-1">
                                    {renderListItem("Duration", data.duration, Clock)}
                                    {renderListItem("Credit Hours", data.creditHours, BookOpen)}
                                    {renderListItem("Coordinator", data.coordinator, User)}
                                </div>
                            ))}

                            {renderSection("Eligibility", (
                                <p className="text-sm text-gray-700 font-medium bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                    {data.admissionCriteria}
                                </p>
                            ))}
                        </div>

                        {renderSection("Key Benefits", (
                            <div className="grid grid-cols-1 gap-3">
                                {data.benefits.map((benefit: string, idx: number) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 bg-green-50/50 rounded-xl border border-green-100 text-green-800 text-sm font-medium">
                                        <CheckCircle2 size={16} className="shrink-0" />
                                        {benefit}
                                    </div>
                                ))}
                            </div>
                        ))}

                        {renderSection("Career Prospects", (
                            <div className="flex items-center gap-4 p-4 bg-amber-50/50 rounded-xl border border-amber-100">
                                <Briefcase size={20} className="text-amber-600" />
                                <p className="text-gray-800 font-medium">{data.careerProspects}</p>
                            </div>
                        ))}
                    </div>
                );

            case 'fee-structure':
                return (
                    <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6 bg-gray-50/50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {renderSection("One-Time Fees", (
                                <div className="space-y-1">
                                    {renderListItem("Admission Fee", data.admissionFee, Award)}
                                    {renderListItem("Processing Fee", data.admissionProcessingFee, FileText)}
                                    {renderListItem("Security Deposit", data.securityDeposit, Building)}
                                </div>
                            ))}

                            {renderSection("Semester Fees", (
                                <div className="space-y-1">
                                    {renderListItem("Tuition Fee", data.tuitionPerSemester, DollarSign)}
                                    {renderListItem("Facilitation Fee", data.facilitationFee, Clock)}
                                    {renderListItem("Exam Fee", data.examFee, FileText)}
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {renderSection("Hostel & Transport", (
                                <div className="space-y-4">
                                    <div className="p-3 bg-gray-100/50 rounded-xl">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Hostel (Per Semester)</p>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Sharing</span>
                                            <span className="font-bold text-gray-900">{data.hostelFees.sharingRoom}</span>
                                        </div>
                                        <div className="flex justify-between text-sm mt-1">
                                            <span className="text-gray-600">Separate</span>
                                            <span className="font-bold text-gray-900">{data.hostelFees.separateRoom}</span>
                                        </div>
                                    </div>
                                    <div className="p-3 bg-gray-100/50 rounded-xl">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Transport (Per Semester)</p>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Gulshan-e-Hadeed</span>
                                            <span className="font-bold text-gray-900">{data.transportFees.gulshanEcadeed}</span>
                                        </div>
                                        <div className="flex justify-between text-sm mt-1">
                                            <span className="text-gray-600">City Route</span>
                                            <span className="font-bold text-gray-900">{data.transportFees.city}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {renderSection("Refund Policy", (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="w-12 h-6 flex items-center justify-center bg-green-100 text-green-700 rounded-md font-bold">100%</span>
                                        <span className="text-gray-700">{data.refundPolicy.fullRefund}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="w-12 h-6 flex items-center justify-center bg-amber-100 text-amber-700 rounded-md font-bold">50%</span>
                                        <span className="text-gray-700">{data.refundPolicy.halfRefund}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="w-12 h-6 flex items-center justify-center bg-red-100 text-red-700 rounded-md font-bold">0%</span>
                                        <span className="text-gray-700">{data.refundPolicy.noRefund}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'admission-requirements':
                return (
                    <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6 bg-gray-50/50">
                        {renderSection("Eligibility Criteria", (
                            <div className="space-y-3">
                                {data.eligibility.map((item: string, idx: number) => (
                                    <div key={idx} className="flex gap-3 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100 text-gray-800 text-sm">
                                        <GraduationCap size={18} className="text-indigo-600 shrink-0" />
                                        {item}
                                    </div>
                                ))}
                            </div>
                        ))}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {renderSection("Required Documents", (
                                <div className="space-y-2">
                                    {data.requiredDocuments.map((doc: string, idx: number) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0" />
                                            {doc}
                                        </div>
                                    ))}
                                </div>
                            ))}

                            {renderSection("Selection Flow", (
                                <div className="space-y-4">
                                    {data.selectionProcess.map((step: string, idx: number) => (
                                        <div key={idx} className="flex items-center gap-3">
                                            <div className="w-6 h-6 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full text-xs font-bold shrink-0">{idx + 1}</div>
                                            <span className="text-sm font-medium text-gray-800">{step}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'admission-schedule':
                const schedule = data.fall2026 || data;
                return (
                    <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6 bg-gray-50/50">
                        {renderSection("Fall 2026 Deadlines", (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                {renderListItem("Admission Opening", schedule.admissionOpening, Calendar)}
                                {renderListItem("Last Date to Apply", schedule.lastDateToApply, Clock)}
                                {renderListItem("Entry Test Date", schedule.entryTest, FileText)}
                                {renderListItem("Interview Schedule", schedule.interview, User)}
                                {renderListItem("Merit List Date", schedule.meritList, GraduationCap)}
                                {renderListItem("Fee Deadline", schedule.feeSubmissionDeadline, DollarSign)}
                                {renderListItem("Classes Commence", schedule.semesterStartDate, Building)}
                            </div>
                        ))}
                    </div>
                );

            case 'placement-info':
                return (
                    <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6 bg-gray-50/50">
                        {renderSection("Career Success", (
                            <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-3xl shadow-xl shadow-indigo-500/30">
                                <div className="space-y-1">
                                    <p className="text-4xl font-black font-sans tracking-tight">{data.placementRatio}</p>
                                    <p className="text-xs font-bold uppercase tracking-widest opacity-80">Placement Ratio</p>
                                </div>
                                <div className="w-px h-12 bg-white/20" />
                                <div className="flex-grow">
                                    <p className="text-sm font-medium leading-tight">Aziz Edu graduates work at Google, Microsoft, Systems Limited, and leading tech companies.</p>
                                </div>
                            </div>
                        ))}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {renderSection("Top Hiring Partners", (
                                <div className="grid grid-cols-1 gap-2">
                                    {data.topHiringPartners.map((company: string, idx: number) => (
                                        <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg text-sm text-gray-700">
                                            <Building size={14} className="text-gray-400" />
                                            {company}
                                        </div>
                                    ))}
                                </div>
                            ))}

                            {renderSection("Success Story", (
                                <div className="p-4 bg-blue-50/30 border border-blue-100/50 rounded-2xl italic text-sm text-blue-900 leading-relaxed">
                                    "{data.successStory}"
                                </div>
                            ))}
                        </div>

                        {renderSection("Industry Industry MoUs", (
                            <div className="flex items-center gap-3 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100 text-indigo-800 text-sm font-bold">
                                <CheckCircle2 size={16} />
                                {data.industryMou}
                            </div>
                        ))}
                    </div>
                );

            case 'contact-info':
                return (
                    <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6 bg-gray-50/50">
                        {renderSection("Campus Details", (
                            <div className="space-y-4">
                                <div className="p-4 bg-gray-100/50 rounded-2xl space-y-3">
                                    {renderListItem("Main Campus", data.campusAddress, MapPin)}
                                    {renderListItem("Department", data.department, Building)}
                                    {renderListItem("Office Timings", data.officeTiming, Clock)}
                                </div>
                                {renderSection("Communication", (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                                            <PhoneCall size={20} className="text-indigo-600 mb-2" />
                                            <p className="text-sm font-bold text-gray-900">{data.contactNumber}</p>
                                            <p className="text-[10px] text-gray-400 uppercase font-black mt-1">Direct Helpline</p>
                                        </div>
                                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                            <GraduationCap size={20} className="text-gray-400 mb-2" />
                                            <p className="text-sm font-bold text-gray-900">{data.recognition}</p>
                                            <p className="text-[10px] text-gray-400 uppercase font-black mt-1">HEC Certification</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                );

            default:
                return <div className="p-10 text-center text-gray-400">Information display unavailable</div>;
        }
    };

    const getHeaderProps = () => {
        switch (type) {
            case 'program-details': return { title: data.name, icon: GraduationCap, subtitle: data.category };
            case 'fee-structure': return { title: "Fee Structure", icon: DollarSign, subtitle: "Financial Breakdown" };
            case 'admission-requirements': return { title: "Eligibility", icon: FileText, subtitle: "Admission Criteria" };
            case 'admission-schedule': return { title: "Calendar", icon: Calendar, subtitle: "Fall 2026 Timeline" };
            case 'placement-info': return { title: "Careers", icon: Briefcase, subtitle: "Placements & Success" };
            case 'contact-info': return { title: "Connect", icon: PhoneCall, subtitle: "Campus & Support" };
            default: return { title: "Information", icon: FileText, subtitle: "Details" };
        }
    };

    const header = getHeaderProps();

    return (
        <div className="h-full flex flex-col bg-white overflow-hidden animate-in fade-in duration-500">
            {renderHeader(header.title, header.icon, header.subtitle)}
            {renderContent()}
        </div>
    );
};

const BookOpen = ({ size, className }: any) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
);
